import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { LalSegmentFields } from '@openx/types/segments.ts';
import { produceSortedOptionsKeys } from '@openx/utils/lib/sortedOptions.ts';
import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getLalSegmentLabels } from '../../../constants.ts';
import { useSegmentContext } from '../../utils/context/SegmentContext.tsx';

type OnBehalfOfFormProps = {
  isEdit: boolean;
  setSelectedAccountId?: (accountId: string) => void;
};

export function OnBehalfOfForm({
  isEdit,
  setSelectedAccountId = () => {
    return undefined;
  },
}: OnBehalfOfFormProps) {
  const { t } = useTranslation();

  const {
    setValue,
    formState: { isSubmitting },
  } = useFormContext();

  const { useAccountOptionsFetch } = useSegmentContext();

  const { accounts } = useAccountOptionsFetch();

  const observableAccountUid = useWatch({ name: LalSegmentFields.ACCOUNT_ID });

  const labels = useMemo(() => getLalSegmentLabels(t), [t]);
  const accountOptions = useMemo(() => produceSortedOptionsKeys(accounts.lalEnabledAccounts), [accounts]);

  const handleOnManageAccountChange = useCallback(
    (_, value) => {
      setValue(LalSegmentFields.ACCOUNT_ID, value);
      setSelectedAccountId(value);
      setValue(LalSegmentFields.PROVIDER, '');
      setValue(LalSegmentFields.SEEDS, []);
      setValue(LalSegmentFields.NAME, '');
    },
    [setSelectedAccountId, setValue]
  );

  return (
    <FormSection
      divider
      title={t('On Behalf Of')}
      description={t('Create LAL Segment on behalf of a different account.')}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
    >
      <RHFAutoCompleteVirtualize
        name={LalSegmentFields.ACCOUNT_ID}
        textFieldProps={{ label: labels[LalSegmentFields.ACCOUNT_ID] }}
        disableClearable
        renderOptions={{
          dataTest: 'lalsegment-account-item',
          tooltip: true,
        }}
        disabled={isEdit || isSubmitting}
        getOptionLabel={option => accounts.mapById[option]?.name || option}
        options={accountOptions}
        loading={accounts.loading}
        value={accounts.loading ? null : observableAccountUid}
        onChange={handleOnManageAccountChange}
        data-test="lalsegment-account"
      />
    </FormSection>
  );
}
